// Reusable smart search logic extracted from SmartSearch component
import { products, Product } from '@/data/products';
import { niches, Niche } from '@/data/niches';

// Semantic map of popular terms
const semanticMap: Record<string, { products: string[]; niches: string[]; keywords: string[] }> = {
  'coceira': { products: ['BCH01', 'BCH02', 'BBH01'], niches: ['dermatite-atopica', 'xerose-idoso'], keywords: ['coceira', 'prurido', 'coçando'] },
  'fissuras': { products: ['BCB01', 'BCH03'], niches: ['inverno-baixa-umidade', 'onco-care'], keywords: ['fissura', 'rachadura', 'rachaduras', 'rachado'] },
  'pele rachada': { products: ['BCB01', 'BCH03', 'BBH01'], niches: ['inverno-baixa-umidade'], keywords: ['rachada', 'rachando', 'ressecamento'] },
  'pós-quimio': { products: ['BCB01', 'BBH01', 'BCS01'], niches: ['onco-care'], keywords: ['quimio', 'quimioterapia', 'oncologico', 'câncer', 'cancer'] },
  'sem perfume': { products: ['BCS01', 'BCH01', 'BBH01', 'BCB01'], niches: ['onco-care', 'maternidade-segura'], keywords: ['perfume', 'fragrância', 'cheiro', 'fragrance'] },
  'pele reativa': { products: ['BCS01', 'BCH01'], niches: ['dermatite-atopica', 'barreira-melasma'], keywords: ['reativa', 'irritada', 'sensível'] },
  'gestante': { products: ['BBH01', 'BCS01', 'BCH01'], niches: ['maternidade-segura'], keywords: ['gestante', 'grávida', 'gravidez', 'gestação'] },
  'mãos': { products: ['BCH02'], niches: ['profissional-saude'], keywords: ['mãos', 'mao', 'maos'] },
  'pés': { products: ['BCH03'], niches: ['inverno-baixa-umidade'], keywords: ['pés', 'pe', 'pes', 'calcanhares'] },
  'idoso': { products: ['BBH01', 'BCH03'], niches: ['xerose-idoso'], keywords: ['idoso', 'terceira idade', 'maduro'] },
  'hospital': { products: ['BBH01', 'BCH02'], niches: ['profissional-saude', 'onco-care'], keywords: ['hospital', 'hospitalar', 'clínica', 'uti'] },
  'barreira': { products: ['BCH01', 'BCB01', 'BBH01'], niches: ['barreira-melasma', 'pos-procedimento'], keywords: ['barreira', 'reconstruir', 'reparar'] },
  'hidratante': { products: ['BCH01', 'BBH01', 'BCH02', 'BCH03'], niches: [], keywords: ['hidratante', 'hidratação', 'hidratar'] },
  'sabonete': { products: ['BCS01'], niches: [], keywords: ['sabonete', 'limpeza', 'limpar', 'banho'] },
  'viagem': { products: ['BCH01', 'BCB01'], niches: ['viagem-neve-altitude'], keywords: ['viagem', 'avião', 'aviao', 'neve'] }
};

export interface SmartSearchResult {
  type: 'product' | 'routine';
  title: string;
  url: string;
  image?: string;
  sku?: string;
  relevance: number;
  matchedTerms: string[];
}

export const SEARCH_SUGGESTIONS = ['coceira', 'pele rachada', 'sem perfume', 'gestante', 'hidratante mãos'];

export function smartSearch(query: string): SmartSearchResult[] {
  if (!query || query.length < 2) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  const results: SmartSearchResult[] = [];
  const matchedProductIds = new Set<string>();
  const matchedNicheIds = new Set<string>();
  
  // 1. Check semantic map
  for (const [term, mapping] of Object.entries(semanticMap)) {
    const keywords = [term, ...mapping.keywords];
    if (keywords.some(k => normalizedQuery.includes(k) || k.includes(normalizedQuery))) {
      // Add matching products
      mapping.products.forEach(productId => {
        if (!matchedProductIds.has(productId)) {
          const product = products.find(p => p.id === productId);
          if (product) {
            matchedProductIds.add(productId);
            results.push({
              type: 'product',
              title: product.shortName,
              url: `/produto/${product.slug}`,
              image: product.images?.[0],
              sku: product.id,
              relevance: 10,
              matchedTerms: [term]
            });
          }
        }
      });
      
      // Add matching niches as routines
      mapping.niches.forEach(nicheSlug => {
        if (!matchedNicheIds.has(nicheSlug)) {
          const niche = niches.find(n => n.slug === nicheSlug);
          if (niche) {
            matchedNicheIds.add(nicheSlug);
            results.push({
              type: 'routine',
              title: niche.title.split('—')[0].trim(),
              url: `/${niche.slug}`,
              relevance: 8,
              matchedTerms: [term]
            });
          }
        }
      });
    }
  }
  
  // 2. Direct text search in products
  products.forEach(product => {
    if (matchedProductIds.has(product.id)) return;
    
    const searchText = `${product.name} ${product.shortName} ${product.description} ${product.tags.join(' ')}`.toLowerCase();
    if (searchText.includes(normalizedQuery)) {
      results.push({
        type: 'product',
        title: product.shortName,
        url: `/produto/${product.slug}`,
        image: product.images?.[0],
        sku: product.id,
        relevance: 5,
        matchedTerms: [normalizedQuery]
      });
    }
  });
  
  // 3. Direct search in niches
  niches.forEach(niche => {
    if (matchedNicheIds.has(niche.slug)) return;
    
    const searchText = `${niche.title} ${niche.hero.headline} ${niche.seo.keywords.join(' ')}`.toLowerCase();
    if (searchText.includes(normalizedQuery)) {
      results.push({
        type: 'routine',
        title: niche.title.split('—')[0].trim(),
        url: `/${niche.slug}`,
        relevance: 4,
        matchedTerms: [normalizedQuery]
      });
    }
  });
  
  // Sort by relevance and limit
  return results.sort((a, b) => b.relevance - a.relevance).slice(0, 12);
}
